import { PropsWithChildren, createContext, useContext, useEffect, useRef } from 'react';
import { VisuallyHidden } from '@mantine/core';

interface LiveRegionContextValue {
  announce: (message: string) => void;
}

const LiveRegionContext = createContext<LiveRegionContextValue | undefined>(undefined);

export function LiveRegionProvider({ children }: PropsWithChildren) {
  const regionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (regionRef.current) {
      regionRef.current.setAttribute('role', 'status');
      regionRef.current.setAttribute('aria-live', 'polite');
      regionRef.current.setAttribute('aria-atomic', 'true');
    }
  }, []);

  const announce = (message: string) => {
    if (regionRef.current) {
      regionRef.current.textContent = '';
      window.requestAnimationFrame(() => {
        if (regionRef.current) {
          regionRef.current.textContent = message;
        }
      });
    }
  };

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      {children}
      <VisuallyHidden>
        <div ref={regionRef} />
      </VisuallyHidden>
    </LiveRegionContext.Provider>
  );
}

export function useLiveRegion() {
  const ctx = useContext(LiveRegionContext);
  if (!ctx) throw new Error('useLiveRegion must be used within LiveRegionProvider');
  return ctx;
}
